
import TimeOfDay from "./TimeOfDay";
import { Guid } from "guid-typescript";

export enum RideType {
    Unknown = 0,
    Arrival = 1,
    Departure = 2
}

export class Ride {
    id: Guid;
    timeOfDay: TimeOfDay;
    timeOfDayUpdate: TimeOfDay;
    fromId: number;
    toId: number;
    fromAddress: string;
    toAddress: string;
    rideType: RideType;
    fromHome: boolean;
    toHome: boolean;
    passengerId: number;
    timetableId: Guid;
    weekDayId: number;
    creatingId?: Guid;
    creating: boolean;
    updating: boolean;
    deleting: boolean;

    constructor(options: Partial<Ride>) {
        this.id = options && options.id ? options.id : Guid.create();
        this.timeOfDay = options && options.timeOfDay ? options.timeOfDay : new TimeOfDay(7, 45);
        this.fromId = options && options.fromId ? options.fromId : 0;
        this.toId = options && options.toId ? options.toId : 0;
        this.fromAddress = options && options.fromAddress ? options.fromAddress :  "Unknown address";
        this.toAddress = options && options.toAddress ? options.toAddress :  "Unknown address";
        this.rideType = options && options.rideType ? options.rideType : RideType.Unknown;
        this.fromHome = options && options.fromHome ? options.fromHome : false;
        this.toHome = options && options.toHome ? options.toHome : false;
        this.passengerId = options && options.passengerId ? options.passengerId : 0;
        this.timetableId = options && options.timetableId ? options.timetableId : null;
        this.weekDayId = options && options.weekDayId ? options.weekDayId : 0;
        // For local change indication/update transaction sync
        this.creatingId = options && options.creatingId ? options.creatingId : undefined;
        this.creating = options && options.creating ? options.creating : false;
        this.updating = options && options.updating ? options.updating : false;
        this.deleting = options && options.deleting ? options.deleting : false;
        this.timeOfDayUpdate = options && options.timeOfDayUpdate ? options.timeOfDayUpdate : undefined;
    }

    get isArrival() {
        return this.rideType === RideType.Arrival; 
    }
    get isDeparture() {
        return this.rideType === RideType.Departure; 
    }
    get value() {
        return this.timeOfDay.value;
    }
    get isValid() {
       return this.timeOfDay !== null
       && (this.fromId > 0 || this.fromHome)
       && (this.toId > 0 || this.toHome)
       && this.passengerId > 0
       && this.timetableId 
       && this.weekDayId >= 0 && this.weekDayId <= 6;
    }
    get loading(){
        return this.creating || this.updating || this.deleting; 
    }

    public static CopyFrom(sourceRide: Ride): Ride {
        return new Ride({
            timeOfDay: new TimeOfDay(sourceRide.timeOfDay.hours, sourceRide.timeOfDay.minutes),
            fromId: sourceRide.fromId,
            toId: sourceRide.toId,
            fromAddress: sourceRide.fromAddress,
            toAddress: sourceRide.toAddress,
            rideType: sourceRide.rideType,
            fromHome: sourceRide.fromHome,
            toHome: sourceRide.toHome,
            passengerId: sourceRide.passengerId,
            timetableId: sourceRide.timetableId,
            weekDayId: sourceRide.weekDayId,
            creatingId: sourceRide.creatingId,
            creating: sourceRide.creating,
            updating: sourceRide.updating,
            deleting: sourceRide.deleting, 
            timeOfDayUpdate: sourceRide.timeOfDayUpdate,
        })
    }

    public static toApiRide(ride: Ride): any {
        const apiRide = (Ride.CopyFrom(ride) as any);
        apiRide.id = ride.id.toString();
        apiRide.creatingId =  ride.creatingId ? ride.creatingId.toString() : undefined;
        const timeOfDay: TimeOfDay =  ride.timeOfDayUpdate || ride.timeOfDay;
        apiRide.hour = timeOfDay.hours;
        apiRide.minute = timeOfDay.minutes;
        delete apiRide.timeOfDay;
        delete apiRide.timeOfDayUpdate;
        delete apiRide.updating;
        delete apiRide.deleting;
        return apiRide;
    }

    public static fromApi(apiRide: any): Ride {
        const ride = new Ride(apiRide);
        ride.timeOfDay = new TimeOfDay(apiRide.hour, apiRide.minute);
        return ride;
    }

    public static sortByTime(rides: Ride[]): Ride[] {
        return rides.sort((a, b) => {
            const timeOfDayA: TimeOfDay = a.timeOfDayUpdate || a.timeOfDay;
            const timeOfDayB: TimeOfDay = b.timeOfDayUpdate || b.timeOfDay;
            if (timeOfDayA.totalMinutes < timeOfDayB.totalMinutes) {
                return -1;
            }
            if (timeOfDayA.totalMinutes > timeOfDayB.totalMinutes) {
                return 1;
            }
            return 0;
        })
    }
}

export default Ride;
